/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import {
    Button,
    ITelemetryContent,
    getPayloadObject,
    getTelemetryAttributes
} from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import classnames from 'classnames';

/**
 * Class names used in component.
 */
const componentClassName = 'ms-order-history-with-search-and-filters-view-mode-selector';
const detailsViewClassName = `${componentClassName}__detailed-view`;
const listViewClassName = `${componentClassName}__list-view`;
const viewSelectedClassName = `${componentClassName}__selected`;

/**
 * IOrderHistoryWithSearchAndFiltersViewModeResources interface.
 */
export interface IOrderHistoryWithSearchAndFiltersViewModeResources {
    listViewOptionAriaLabel?: string;
    detailedViewOptionAriaLabel?: string;
}

/**
 * OrderHistoryWithSearchAndFiltersViewModeState interface.
 */
export enum OrderHistoryWithSearchAndFiltersViewModeState {
    DetailedView,
    ListView
}

/**
 * IOrderHistoryWithSearchAndFiltersViewModeProps interface.
 */
export interface IOrderHistoryWithSearchAndFiltersViewModeProps extends IComponentProps<{}> {
    className?: string;
    resources?: IOrderHistoryWithSearchAndFiltersViewModeResources;
    currentViewModeState: OrderHistoryWithSearchAndFiltersViewModeState;
    telemetryContent?: ITelemetryContent;
    onClick(state: OrderHistoryWithSearchAndFiltersViewModeState): void;
}

/**
 * OrderHistoryWithSearchAndFiltersViewMode component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const OrderHistoryWithSearchAndFiltersViewModeFunctionalComponent: React.FC<IOrderHistoryWithSearchAndFiltersViewModeProps> = (
    props: IOrderHistoryWithSearchAndFiltersViewModeProps
) => {
    const [viewModeState, setViewModeState] = React.useState(props.currentViewModeState);

    const onClickHandler = (newViewModeState: OrderHistoryWithSearchAndFiltersViewModeState) => {
        setViewModeState(newViewModeState);
        props.onClick(newViewModeState);
    };

    // define telemetry attributes
    const viewModeDetailedViewAttributes = getTelemetryAttributes(
        props.telemetryContent!,
        getPayloadObject('click', props.telemetryContent!, `View mode - Detailed view`)
    );
    const viewModeListViewAttributes = getTelemetryAttributes(
        props.telemetryContent!,
        getPayloadObject('click', props.telemetryContent!, `View mode - List view`)
    );

    return (
        <div className={classnames(props.className, componentClassName)}>
            <Button
                className={classnames(
                    detailsViewClassName,
                    viewModeState === OrderHistoryWithSearchAndFiltersViewModeState.DetailedView ? viewSelectedClassName : ''
                )}
                aria-label={props.resources?.detailedViewOptionAriaLabel ?? ''}
                title={props.resources?.detailedViewOptionAriaLabel ?? ''}
                role='button'
                onClick={() => onClickHandler(OrderHistoryWithSearchAndFiltersViewModeState.DetailedView)}
                {...viewModeDetailedViewAttributes}
            />
            <Button
                className={classnames(
                    listViewClassName,
                    viewModeState === OrderHistoryWithSearchAndFiltersViewModeState.ListView ? viewSelectedClassName : ''
                )}
                aria-label={props.resources?.listViewOptionAriaLabel ?? ''}
                title={props.resources?.listViewOptionAriaLabel ?? ''}
                role='button'
                onClick={() => onClickHandler(OrderHistoryWithSearchAndFiltersViewModeState.ListView)}
                {...viewModeListViewAttributes}
            />
        </div>
    );
};

/**
 * OrderHistoryWithSearchAndFiltersViewMode component.
 */
export const OrderHistoryWithSearchAndFiltersViewModeComponent: React.FunctionComponent<IOrderHistoryWithSearchAndFiltersViewModeProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IOrderHistoryWithSearchAndFiltersViewModeProps
>('OrderHistoryWithSearchAndFiltersViewModeComponent', { component: OrderHistoryWithSearchAndFiltersViewModeFunctionalComponent });
